import { Outlet, Link, useLocation } from "react-router-dom";
import { 
  Home, Building2, Users, Calendar, DollarSign, Settings, LogOut, Menu, X, 
  ChevronDown, ChevronRight, AlertCircle, UserPlus, Mail, FileText, UserCheck, 
  Send, FilePen, Clock, ClipboardCheck, Shield, Utensils, Moon, Brain, Palette, 
  GraduationCap, Briefcase, TrendingUp, BookOpen, Eye, Sparkles, Layers, Phone, 
  LayoutGrid, BarChart3, Download, MessageSquare, Target, CreditCard, Bell, Heart,
  UserCircle, Upload, Trash2, History
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState, useEffect, useMemo } from "react";
import GlobalSearch from "@/components/GlobalSearch";
import StaffNotificationBell from "@/components/StaffNotificationBell";
import { usePermissions, PermissionsProvider } from "@/contexts/PermissionsContext";

function TenantAdminLayoutContent({ user, tenant, centers, onLogout }) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState("all");
  const [expandedSections, setExpandedSections] = useState({});
  const { canAccessNav, isAdmin, assignedCenters } = usePermissions();

  // Dynamic page title based on route
  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    let title = "Dashboard";
    if (pathSegments.length > 0) {
      title = pathSegments[pathSegments.length - 1]
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    document.title = `${title} | ConnectEd`;
  }, [location.pathname]);

  // Filter centers based on user's access
  const accessibleCenters = useMemo(() => {
    if (isAdmin) return centers;
    if (assignedCenters.length > 0) {
      const assignedIds = assignedCenters.map(c => c.id);
      return centers.filter(c => assignedIds.includes(c.id));
    }
    return centers;
  }, [centers, isAdmin, assignedCenters]);

  // Set default center for staff users
  useEffect(() => {
    if (!isAdmin && assignedCenters.length > 0 && selectedCenter === "all") {
      setSelectedCenter(assignedCenters[0].id);
    }
  }, [isAdmin, assignedCenters, selectedCenter]);

  // Auto-expand section based on current path
  useEffect(() => {
    const path = location.pathname;
    const sectionMap = {
      '/daily-logs': 'operations',
      '/curriculum': 'learning',
      '/crm': 'crm',
      '/enrollment': 'crm',
      '/billing': 'finance',
      '/email': 'communication',
      '/staff': 'staff',
      '/analytics': 'admin',
      '/centers': 'admin',
      '/sessions': 'admin',
      '/compliance': 'admin',
      '/settings': 'settings',
    };
    
    for (const [prefix, section] of Object.entries(sectionMap)) {
      if (path.startsWith(prefix)) {
        setExpandedSections(prev => ({ ...prev, [section]: true }));
        break;
      }
    }
  }, [location.pathname]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Navigation Structure
  const navSections = [
    {
      id: "main",
      title: "MAIN",
      items: [
        { name: "Dashboard", href: "/", icon: Home, permKey: "dashboard" },
        { name: "Attendance", href: "/attendance", icon: ClipboardCheck, permKey: "attendance" },
      ]
    },
    {
      id: "operations",
      title: "OPERATIONS",
      collapsible: true,
      items: [
        { name: "Children", href: "/children", icon: Users, permKey: "children" },
        { name: "Parents", href: "/parents", icon: Users, permKey: "parents" },
        { name: "Rooms", href: "/rooms", icon: Building2, permKey: "rooms" },
        { name: "Programs", href: "/programs", icon: Calendar, permKey: "programs" },
      ],
      subSections: [
        {
          title: "Daily Logs",
          items: [
            { name: "Log Activity", href: "/daily-logs", icon: Utensils, permKey: "daily-logs" },
            { name: "Activity History", href: "/activities/reports", icon: History, permKey: "daily-logs" },
            { name: "Nap Tracker", href: "/daily-logs/naps", icon: Moon, permKey: "daily-logs-naps" },
            { name: "Mood Reports", href: "/daily-logs/mood-reports", icon: Brain, permKey: "daily-logs-mood" },
          ]
        }
      ]
    },
    {
      id: "communication",
      title: "COMMUNICATION",
      collapsible: true,
      items: [
        { name: "Parent Messages", href: "/messages", icon: MessageSquare, permKey: "email-compose" },
        { name: "Comment Feed", href: "/comments", icon: Bell, permKey: "email-compose" },
        { name: "Activity Engagement", href: "/activity-engagement", icon: Heart, permKey: "email-compose" },
        { name: "Compose Email", href: "/email/compose", icon: FilePen, permKey: "email-compose" },
        { name: "Sent", href: "/email/sent", icon: Send, permKey: "email-sent" },
        { name: "Scheduled", href: "/email/scheduled", icon: Clock, permKey: "email-sent" },
        { name: "Drafts", href: "/email/drafts", icon: FileText, permKey: "email-drafts" },
      ]
    },
    {
      id: "learning",
      title: "LEARNING",
      collapsible: true,
      items: [
        { name: "Lesson Plans", href: "/curriculum/lessons", icon: BookOpen, permKey: "curriculum-lessons" },
        { name: "AI Planner", href: "/curriculum/ai-planner", icon: Sparkles, permKey: "curriculum-ai-planner" },
        { name: "Observations", href: "/curriculum/observations", icon: Eye, permKey: "curriculum-observations" },
        { name: "Work Samples", href: "/curriculum/work-samples", icon: Palette, permKey: "curriculum-work-samples" },
        { name: "Milestones", href: "/curriculum/milestones", icon: Target, permKey: "curriculum-lessons" },
        { name: "Curriculum Settings", href: "/curriculum/settings", icon: Layers, permKey: "curriculum-settings" },
      ]
    },
    {
      id: "crm",
      title: "ENROLLMENT & CRM",
      collapsible: true,
      items: [
        { name: "CRM Dashboard", href: "/crm", icon: LayoutGrid, permKey: "crm" },
        { name: "Enrollment", href: "/enrollment", icon: UserPlus, permKey: "enrollment" },
        { name: "Leads", href: "/crm/leads", icon: Users, permKey: "crm-leads" },
        { name: "Pipeline", href: "/crm/pipeline", icon: TrendingUp, permKey: "crm-pipeline" },
        { name: "Tours", href: "/crm/tours", icon: Calendar, permKey: "crm-tours" },
        { name: "Follow-ups", href: "/crm/followups", icon: Phone, permKey: "crm-followups" },
      ]
    },
    {
      id: "finance",
      title: "FINANCE",
      collapsible: true,
      items: [
        { name: "Invoices", href: "/billing/invoices", icon: FileText, permKey: "billing-invoices" },
        { name: "Payments", href: "/billing/payments", icon: DollarSign, permKey: "billing-payments" },
        { name: "Outstanding", href: "/billing/outstanding", icon: CreditCard, permKey: "billing-invoices" },
        { name: "Billing Settings", href: "/billing/settings", icon: Settings, permKey: "billing-settings" },
      ]
    },
    {
      id: "staff",
      title: "STAFF",
      collapsible: true,
      items: [
        { name: "My Profile", href: "/my-profile", icon: UserCircle, staffSelf: true },
        { name: "Staff Dashboard", href: "/staff-management", icon: Briefcase, permKey: "staff-management" },
        { name: "Staff Directory", href: "/staff", icon: Users, permKey: "staff-directory" },
        { name: "Staff Attendance", href: "/staff/attendance", icon: Clock, permKey: "staff-attendance" },
        { name: "Training", href: "/staff/training", icon: GraduationCap, permKey: "staff-training" },
        { name: "Leave Requests", href: "/staff/leave-requests", icon: Calendar, permKey: "staff-leave" },
        { name: "Performance", href: "/staff/performance-settings", icon: TrendingUp, permKey: "staff-performance" },
      ]
    },
    {
      id: "admin",
      title: "ADMIN",
      collapsible: true,
      adminOnly: true,
      items: [
        { name: "Centers", href: "/centers", icon: Building2, permKey: "centers" },
        { name: "Sessions", href: "/sessions", icon: Settings, permKey: "sessions" },
        { name: "Compliance Reports", href: "/compliance-reports", icon: AlertCircle, permKey: "compliance" },
      ],
      subSections: [
        {
          title: "Analytics",
          items: [
            { name: "Dashboard", href: "/analytics", icon: BarChart3, permKey: "analytics" },
            { name: "Enrollment", href: "/analytics/enrollment", icon: Users, permKey: "analytics" },
            { name: "Attendance", href: "/analytics/attendance", icon: ClipboardCheck, permKey: "analytics" },
            { name: "Financial", href: "/analytics/financial", icon: DollarSign, permKey: "analytics" },
            { name: "Occupancy", href: "/analytics/occupancy", icon: Building2, permKey: "analytics" },
            { name: "CRM", href: "/analytics/crm", icon: Briefcase, permKey: "analytics" },
            { name: "Staff", href: "/analytics/staff", icon: UserCheck, permKey: "analytics" },
            { name: "Export", href: "/analytics/export", icon: Download, permKey: "analytics-export" },
            { name: "Scheduled", href: "/analytics/scheduled", icon: Clock, permKey: "analytics" },
          ]
        }
      ]
    },
    {
      id: "settings",
      title: "SETTINGS",
      collapsible: true,
      adminOnly: true,
      items: [
        { name: "General", href: "/settings/general", icon: Settings, permKey: "settings-general" },
        { name: "Permissions", href: "/settings/permissions", icon: Shield, permKey: "settings-permissions" },
        { name: "Activity Types", href: "/settings/activity-types", icon: Palette, permKey: "settings-activity-types" },
        { name: "Email Templates", href: "/settings/email-templates", icon: FileText, permKey: "settings-email-templates" },
        { name: "Email Settings", href: "/settings/email", icon: Mail, permKey: "settings-email" },
        { name: "Parent Invitations", href: "/settings/parent-invitations", icon: UserCheck, permKey: "settings-parent-invitations" },
        { name: "Bulk Import", href: "/settings/bulk-import", icon: Upload, permKey: "settings-bulk-import" },
        { name: "Children Cleanup", href: "/settings/children-cleanup", icon: Trash2, permKey: "settings-children-cleanup" },
      ]
    },
  ];

  // Filter items based on permissions
  const filterItems = (items) => {
    return items.filter(item => {
      if (item.disabled) return true; // Show disabled items but grayed out
      if (item.staffSelf) return true; // Always show self-service items for all users
      return canAccessNav(item.permKey);
    });
  };

  // Check if section should be visible
  const isSectionVisible = (section) => {
    if (section.adminOnly && !isAdmin) return false;
    
    const visibleItems = filterItems(section.items);
    const hasVisibleSubItems = section.subSections?.some(sub => 
      filterItems(sub.items).length > 0
    );
    
    return visibleItems.length > 0 || hasVisibleSubItems;
  };

  // Render a nav item
  const renderNavItem = (item) => {
    const isActive = location.pathname === item.href || 
      (item.href !== "/" && location.pathname.startsWith(item.href));
    
    if (item.disabled) {
      return (
        <div
          key={item.name}
          className="flex items-center gap-2 px-3 py-1.5 text-xs text-slate-400 cursor-not-allowed"
        >
          <item.icon className="w-4 h-4" />
          <span>{item.name}</span>
          <span className="ml-auto text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">Soon</span>
        </div>
      );
    }
    
    return (
      <Link
        key={item.name}
        to={item.href}
        data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
        className={`flex items-center gap-2 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
          isActive
            ? "bg-sage-100 text-sage-700"
            : "text-slate-600 hover:bg-slate-50 hover:text-sage-600"
        }`}
        onClick={() => setSidebarOpen(false)}
      >
        <item.icon className="w-4 h-4" />
        <span>{item.name}</span>
      </Link>
    );
  };

  // Render a section
  const renderSection = (section) => {
    if (!isSectionVisible(section)) return null;
    
    const isExpanded = expandedSections[section.id] ?? !section.collapsible;
    const filteredItems = filterItems(section.items);
    
    return (
      <div key={section.id} className="mb-1">
        {section.collapsible ? (
          <button
            onClick={() => toggleSection(section.id)}
            className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider hover:text-slate-600"
          >
            <span>{section.title}</span>
            {isExpanded ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )}
          </button>
        ) : (
          <p className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
            {section.title}
          </p>
        )}
        
        {isExpanded && (
          <div className="space-y-0.5">
            {filteredItems.map(renderNavItem)}
            
            {section.subSections?.map((sub, idx) => {
              const subItems = filterItems(sub.items);
              if (subItems.length === 0) return null;
              
              return (
                <div key={idx} className="mt-1">
                  <p className="px-3 py-1 text-[10px] font-medium text-slate-400">
                    {sub.title}
                  </p>
                  <div className="pl-2 space-y-0.5">
                    {subItems.map(renderNavItem)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile header */}
      <div className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(true)} className="p-2">
          <Menu className="w-6 h-6 text-slate-600" />
        </button>
        <img src="/connected-logo.png" alt="ConnectEd" className="h-10" />
        <div className="w-10" />
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-56 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="px-4 py-4 border-b border-slate-100 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/connected-logo.png" alt="ConnectEd" className="h-12" />
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 hover:bg-slate-100 rounded"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto scrollbar-thin">
            {navSections.map(renderSection)}
          </nav>

          {/* User section */}
          <div className="border-t border-slate-100 p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-sage-100 flex items-center justify-center">
                <span className="text-xs font-medium text-sage-700">
                  {user?.name?.charAt(0) || "U"}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-700 truncate">
                  {user?.name || "User"}
                </p>
                <p className="text-[10px] text-slate-500 truncate">
                  {tenant?.name || "Childcare"}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              className="w-full justify-start text-xs text-slate-600 hover:text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:pl-56">
        {/* Top bar */}
        <header className="bg-white border-b border-slate-200 px-4 lg:px-6 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 max-w-md">
              <GlobalSearch />
            </div>
            <div className="flex items-center gap-3">
              <StaffNotificationBell />
              
              {/* Center Switcher */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="center-switcher"
                    className="text-xs"
                  >
                    <Building2 className="w-4 h-4 mr-1" />
                    {selectedCenter === "all" 
                      ? "All Centers" 
                      : accessibleCenters.find(c => c.id === selectedCenter)?.name || "Select"}
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {isAdmin && (
                    <DropdownMenuItem
                      onClick={() => setSelectedCenter("all")}
                      className={selectedCenter === "all" ? "bg-sage-50" : ""}
                    >
                      All Centers
                    </DropdownMenuItem>
                  )}
                  {accessibleCenters.map((center) => (
                    <DropdownMenuItem
                      key={center.id}
                      onClick={() => setSelectedCenter(center.id)}
                      className={selectedCenter === center.id ? "bg-sage-50" : ""}
                    >
                      {center.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="p-4 lg:p-6">
          <Outlet context={{ selectedCenter, centers: accessibleCenters }} />
        </div>
      </main>
    </div>
  );
}

// Wrapper component that provides the PermissionsContext
export default function TenantAdminLayout({ user, tenant, centers, onLogout }) {
  return (
    <PermissionsProvider user={user}>
      <TenantAdminLayoutContent 
        user={user} 
        tenant={tenant} 
        centers={centers} 
        onLogout={onLogout} 
      />
    </PermissionsProvider>
  );
}
